import React, { useState, useRef, useMemo, useCallback } from 'react';
import Map, { Source, Layer, NavigationControl, Marker } from 'react-map-gl/maplibre';
import { getViewModeStyle } from '../lib/map-styles';
import {
  computeAffineTransform, computeResiduals, imageCornerCoordinates,
  exportKMZ, exportWorldFile,
} from '../lib/georeference';
import { useTheme } from '../contexts/ThemeContext';

const GCP_COLORS = ['#00d4ff', '#34d399', '#fbbf24', '#f87171', '#a78bfa', '#38bdf8', '#fb923c', '#4ade80', '#e879f9', '#facc15'];

/**
 * GeoreferencingTool — image → map georeferencing with GCPs (Sprint 7.1, G44)
 *
 * Split view: left = uploaded image (zoom/pan), right = MapLibre map.
 * Click the image to set a source point, then click the map to set its target.
 * ≥3 GCPs → live affine transform, overlay preview, residuals, KMZ/world file export.
 *
 * Props:
 *   initialCenter - { lat, lon } map start position
 *   onClose()
 */
export default function GeoreferencingTool({ initialCenter = { lat: 41.99, lon: -70.15 }, onClose }) {
  const { C: themeC, theme } = useTheme();
  const C = { ...themeC, amber: themeC.warnText };
  const [image, setImage] = useState(null); // { url, file, name, width, height }
  const [gcps, setGcps] = useState([]);     // [{ px, py, lon, lat }]
  const [pendingPx, setPendingPx] = useState(null); // { px, py } waiting for a map click
  const [overlayOpacity, setOverlayOpacity] = useState(0.7);
  const [showOverlay, setShowOverlay] = useState(true);
  const [imgZoom, setImgZoom] = useState(1);
  const [imgPan, setImgPan] = useState({ x: 0, y: 0 });
  const [exporting, setExporting] = useState(false);
  const dragState = useRef(null);
  const imgContainerRef = useRef(null);

  // ── Transform (live, recomputed as GCPs change) ──
  const transform = useMemo(() => computeAffineTransform(gcps), [gcps]);
  const { residuals, rms } = useMemo(() => computeResiduals(transform, gcps), [transform, gcps]);
  const corners = useMemo(
    () => (transform && image ? imageCornerCoordinates(transform, image.width, image.height) : null),
    [transform, image]
  );

  // ── Image upload ──
  const handleFile = (file) => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      setImage({ url, file, name: file.name, width: img.naturalWidth, height: img.naturalHeight });
      setGcps([]);
      setPendingPx(null);
      setImgZoom(1);
      setImgPan({ x: 0, y: 0 });
    };
    img.src = url;
  };

  // ── Image panel interactions ──
  const handleImgWheel = (e) => {
    const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    setImgZoom(z => Math.max(0.05, Math.min(20, z * factor)));
  };

  const handleImgMouseDown = (e) => {
    dragState.current = { x: e.clientX, y: e.clientY, panX: imgPan.x, panY: imgPan.y, moved: false };
  };

  const handleImgMouseMove = (e) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.x;
    const dy = e.clientY - dragState.current.y;
    if (Math.abs(dx) + Math.abs(dy) > 3) dragState.current.moved = true;
    if (dragState.current.moved) {
      setImgPan({ x: dragState.current.panX + dx, y: dragState.current.panY + dy });
    }
  };

  const handleImgMouseUp = (e) => {
    const wasDrag = dragState.current?.moved;
    dragState.current = null;
    if (wasDrag || !image) return;
    // Click → pixel coordinate (account for pan/zoom transform)
    const rect = imgContainerRef.current.getBoundingClientRect();
    const px = (e.clientX - rect.left - imgPan.x) / imgZoom;
    const py = (e.clientY - rect.top - imgPan.y) / imgZoom;
    if (px < 0 || py < 0 || px > image.width || py > image.height) return;
    setPendingPx({ px: Math.round(px * 10) / 10, py: Math.round(py * 10) / 10 });
  };

  // ── Map click → complete the pending GCP ──
  const handleMapClick = useCallback((evt) => {
    if (!pendingPx) return;
    setGcps(prev => [...prev, { ...pendingPx, lon: evt.lngLat.lng, lat: evt.lngLat.lat }]);
    setPendingPx(null);
  }, [pendingPx]);

  const deleteGcp = (i) => setGcps(prev => prev.filter((_, idx) => idx !== i));

  // ── Exports ──
  const handleExportKMZ = async () => {
    if (!corners || !image) return;
    setExporting(true);
    try {
      await exportKMZ(image.name.replace(/\.[^.]+$/, ''), image.file, image.name, corners);
    } finally {
      setExporting(false);
    }
  };

  const selBtn = (active) => ({
    fontSize: 11, padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
    background: active ? C.accentBg : C.bg4,
    color: active ? C.accent : C.text1,
    border: `1px solid ${active ? C.accentBorder : C.border}`,
  });

  return (
    <div style={{ position: 'fixed', inset: 0, background: C.bg0, zIndex: 200, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ height: 48, background: C.bg1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', padding: '0 16px', gap: 12, flexShrink: 0 }}>
        <span style={{ fontSize: 16 }}>🗺️</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: C.text0 }}>Georeferencing Tool</span>
        <span style={{ fontSize: 11, color: C.text3 }}>
          {!image ? 'Upload an image to begin'
            : pendingPx ? '→ Now click the MAP to set the target location'
            : 'Click the IMAGE to place a control point'}
        </span>
        <div style={{ flex: 1 }} />
        {transform && (
          <span style={{ fontSize: 11, color: rms < 5 ? C.emerald : rms < 20 ? C.amber : C.red }}>
            RMS: {rms.toFixed(2)} m
          </span>
        )}
        <span style={{ fontSize: 11, color: C.text3 }}>{gcps.length} GCPs {gcps.length < 3 ? `(need ${3 - gcps.length} more)` : ''}</span>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <button onClick={handleExportKMZ} disabled={!corners || exporting} style={{ ...selBtn(false), opacity: corners ? 1 : 0.4 }}>
          {exporting ? '⏳' : '📦'} KMZ
        </button>
        <button onClick={() => transform && image && exportWorldFile(transform, image.name)} disabled={!transform} style={{ ...selBtn(false), opacity: transform ? 1 : 0.4 }}>
          🌐 World File
        </button>
        <button onClick={onClose} style={{ fontSize: 14, background: 'none', border: 'none', color: C.text3, cursor: 'pointer' }}>✕</button>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left: image panel ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', borderRight: `2px solid ${C.border}`, minWidth: 0 }}>
          <div style={{ padding: '6px 12px', background: C.bg1, borderBottom: `1px solid ${C.border}`, display: 'flex', alignItems: 'center', gap: 10 }}>
            <label style={{ ...selBtn(false), display: 'inline-block' }}>
              📁 {image ? image.name : 'Upload Image (PNG/JPG/TIFF)'}
              <input type="file" accept="image/png,image/jpeg,image/tiff,image/gif" style={{ display: 'none' }}
                onChange={e => handleFile(e.target.files?.[0])} />
            </label>
            {image && (
              <>
                <span style={{ fontSize: 10, color: C.text2 }}>{image.width}×{image.height}px · zoom {(imgZoom * 100).toFixed(0)}%</span>
                <button onClick={() => { setImgZoom(1); setImgPan({ x: 0, y: 0 }); }} style={selBtn(false)}>Fit</button>
              </>
            )}
          </div>
          <div
            ref={imgContainerRef}
            onWheel={handleImgWheel}
            onMouseDown={handleImgMouseDown}
            onMouseMove={handleImgMouseMove}
            onMouseUp={handleImgMouseUp}
            onMouseLeave={() => { dragState.current = null; }}
            style={{ flex: 1, overflow: 'hidden', position: 'relative', cursor: pendingPx ? 'default' : 'crosshair', background: '#0a0f1d' }}
          >
            {!image ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 8, color: C.text3 }}>
                <div style={{ fontSize: 40 }}>🖼️</div>
                <div style={{ fontSize: 13 }}>Upload a map scan, aerial photo, or site plan</div>
                <div style={{ fontSize: 11 }}>Then click matching points on the image and the map</div>
              </div>
            ) : (
              <div style={{ position: 'absolute', transform: `translate(${imgPan.x}px, ${imgPan.y}px)`, transformOrigin: '0 0' }}>
                <img
                  src={image.url} alt="georeference source" draggable={false}
                  style={{ width: image.width * imgZoom, height: image.height * imgZoom, imageRendering: imgZoom > 2 ? 'pixelated' : 'auto', userSelect: 'none' }}
                />
                {/* GCP markers on image */}
                {gcps.map((g, i) => (
                  <div key={i} style={{
                    position: 'absolute', left: g.px * imgZoom - 9, top: g.py * imgZoom - 9,
                    width: 18, height: 18, borderRadius: '50%',
                    background: `${GCP_COLORS[i % GCP_COLORS.length]}33`,
                    border: `2px solid ${GCP_COLORS[i % GCP_COLORS.length]}`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 9, fontWeight: 700, color: GCP_COLORS[i % GCP_COLORS.length],
                    pointerEvents: 'none',
                  }}>{i + 1}</div>
                ))}
                {/* Pending source point */}
                {pendingPx && (
                  <div style={{
                    position: 'absolute', left: pendingPx.px * imgZoom - 9, top: pendingPx.py * imgZoom - 9,
                    width: 18, height: 18, borderRadius: '50%',
                    border: `2px dashed ${C.amber}`, background: `${C.amber}22`,
                    pointerEvents: 'none', animation: 'pulse 1.2s infinite',
                  }} />
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Right: map panel ── */}
        <div style={{ flex: 1, position: 'relative', minWidth: 0 }}>
          <Map
            initialViewState={{ longitude: initialCenter.lon, latitude: initialCenter.lat, zoom: 10 }}
            mapStyle={getViewModeStyle('map', theme)}
            style={{ width: '100%', height: '100%' }}
            onClick={handleMapClick}
            cursor={pendingPx ? 'crosshair' : 'grab'}
            attributionControl={{ compact: true }}
          >
            <NavigationControl position="top-right" />

            {/* Georeferenced image overlay */}
            {corners && showOverlay && (
              <Source id="georef-overlay" type="image" url={image.url} coordinates={corners}>
                <Layer id="georef-overlay-layer" type="raster" paint={{ 'raster-opacity': overlayOpacity, 'raster-fade-duration': 0 }} />
              </Source>
            )}

            {/* GCP target markers */}
            {gcps.map((g, i) => (
              <Marker key={i} longitude={g.lon} latitude={g.lat} anchor="center">
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: `${GCP_COLORS[i % GCP_COLORS.length]}33`,
                  border: `2px solid ${GCP_COLORS[i % GCP_COLORS.length]}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 9, fontWeight: 700, color: GCP_COLORS[i % GCP_COLORS.length],
                }}>{i + 1}</div>
              </Marker>
            ))}
          </Map>

          {/* Overlay controls */}
          {corners && (
            <div style={{
              position: 'absolute', bottom: 12, left: 12, zIndex: 5,
              background: 'rgba(13,17,23,0.9)', border: `1px solid ${C.border}`,
              borderRadius: 8, padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <button onClick={() => setShowOverlay(v => !v)} style={selBtn(showOverlay)}>
                {showOverlay ? '👁 Overlay On' : '👁 Overlay Off'}
              </button>
              <span style={{ fontSize: 10, color: C.text2 }}>Opacity</span>
              <input type="range" min={0.1} max={1} step={0.05} value={overlayOpacity}
                onChange={e => setOverlayOpacity(parseFloat(e.target.value))}
                style={{ width: 90, accentColor: C.accent }} />
            </div>
          )}
        </div>

        {/* ── GCP table sidebar ── */}
        <div style={{ width: 250, background: C.bg1, borderLeft: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12, fontWeight: 600, color: C.text0 }}>
            Ground Control Points
          </div>
          <div style={{ flex: 1, overflow: 'auto', padding: 8 }}>
            {gcps.length === 0 ? (
              <div style={{ fontSize: 11, color: C.text3, padding: '16px 8px', textAlign: 'center', lineHeight: 1.6 }}>
                1. Click a recognizable spot on the <b>image</b><br />
                2. Click the same spot on the <b>map</b><br />
                3. Repeat for ≥3 points (spread them out)
              </div>
            ) : (
              gcps.map((g, i) => (
                <div key={i} style={{ padding: '7px 9px', marginBottom: 4, background: C.bg3, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', background: GCP_COLORS[i % GCP_COLORS.length], display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 8, fontWeight: 700, color: '#0d1117' }}>{i + 1}</span>
                    <span style={{ fontSize: 10, color: C.text1, flex: 1 }}>GCP {i + 1}</span>
                    {residuals[i] !== undefined && (
                      <span style={{ fontSize: 9, color: residuals[i] < 5 ? C.emerald : residuals[i] < 20 ? C.amber : C.red }}>
                        {residuals[i].toFixed(1)}m
                      </span>
                    )}
                    <button onClick={() => deleteGcp(i)} style={{ background: 'none', border: 'none', color: C.text3, cursor: 'pointer', fontSize: 11 }}>🗑</button>
                  </div>
                  <div style={{ fontSize: 9, color: C.text3, fontFamily: 'monospace' }}>
                    px: {g.px}, {g.py}<br />
                    {g.lat.toFixed(6)}°, {g.lon.toFixed(6)}°
                  </div>
                </div>
              ))
            )}
          </div>
          {transform && (
            <div style={{ padding: '10px 12px', borderTop: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 9, color: C.text3, textTransform: 'uppercase', marginBottom: 4 }}>Affine Transform</div>
              <div style={{ fontSize: 9, color: C.text2, fontFamily: 'monospace', lineHeight: 1.5 }}>
                a: {transform.a.toExponential(3)}<br />
                b: {transform.b.toExponential(3)}<br />
                c: {transform.c.toFixed(6)}<br />
                d: {transform.d.toExponential(3)}<br />
                e: {transform.e.toExponential(3)}<br />
                f: {transform.f.toFixed(6)}
              </div>
              <div style={{ fontSize: 10, marginTop: 6, color: rms < 5 ? C.emerald : rms < 20 ? C.amber : C.red }}>
                Total RMS error: {rms.toFixed(2)} m
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
